/** Thin React wrapper around josdejong/jsoneditor (vanilla JS).
 *
 * The editor is created once on mount and destroyed on unmount. Its current
 * value is retrieved imperatively via the forwarded ref — this avoids
 * fighting the editor's own undo/cursor state with controlled-input resets.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
// jsoneditor ships no TypeScript types; we cast through unknown.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JsonEditorLib = any;

export interface JsonEditorHandle {
  /** Returns the current JSON value. Throws if the content is invalid JSON. */
  getValue(): Record<string, unknown>;
}

interface Props {
  initialValue: Record<string, unknown> | null;
  readOnly?: boolean;
  /** Initial editor mode (user can still switch via the menu bar).
   *  'view' (default) for browsing; 'code' for raw-JSON editing dialogs. */
  mode?: 'view' | 'tree' | 'code';
}

export const JsonEditorWidget = forwardRef<JsonEditorHandle, Props>(
  ({ initialValue, readOnly = false, mode = 'view' }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<JsonEditorLib>(null);

    useImperativeHandle(ref, () => ({
      getValue() {
        if (!editorRef.current) return {};
        return editorRef.current.get() as Record<string, unknown>;
      },
    }));

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      // Guard against React Strict Mode's double-invocation: the cleanup
      // fires before the async import resolves, so we need an explicit flag
      // to prevent the second invocation from creating a second editor.
      let cancelled = false;

      import('jsoneditor').then((mod) => {
        if (cancelled) return;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const JSONEditor = (mod.default ?? mod) as new (
          container: HTMLElement,
          options: Record<string, unknown>,
        ) => JsonEditorLib;

        import('jsoneditor/dist/jsoneditor.min.css').catch(() => {/* css-only import */});

        const editor = new JSONEditor(container, {
          mode: readOnly && mode === 'code' ? 'view' : mode,
          modes: readOnly ? ['view', 'tree'] : ['view', 'tree', 'code'],
          mainMenuBar: true,
          navigationBar: false,
          statusBar: true,
        });
        editor.set(initialValue ?? {});
        editorRef.current = editor;
      }).catch(console.error);

      return () => {
        cancelled = true;
        if (editorRef.current) {
          editorRef.current.destroy();
          editorRef.current = null;
        }
        // Also clear any partial DOM left by an in-flight import.
        container.innerHTML = '';
      };
      // Intentionally empty deps — create once, never re-init.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
  },
);
JsonEditorWidget.displayName = 'JsonEditorWidget';
